/**
 * Social-link adapter — first concrete adapter for the element-insertion engine.
 *
 * Recognises `.cs-social-link` repeat units (the CodeStitch idiom: an <a>
 * with class cs-social-link wrapping a single icon, all sitting inside a
 * <div class="cs-social"> or similar parent). Builds new sibling anchors
 * with an inline <svg class="cs-social-img"> per the social-icon-library.
 *
 * Platform data is loaded once at bootstrap via setSocialPlatforms() — the
 * caller (shell.js) fetches /data/social-icon-library.json and passes the
 * `platforms` array in. Tests call setSocialPlatforms() directly with a
 * fixture. SVG markup is sanitized at set time so contentRecipe() never
 * re-parses untrusted source.
 */

import { parseSvg, sanitizeSvg, serializeSvg } from '../svg-sanitizer.js';
import { sanitizeHref } from '../../core/sanitizer.js';
import { moveSiblingPreservingEids } from '../element-insert.js';

/** Internal registry of sanitized platform entries, keyed by id. */
const _platformsById = new Map();
let _platformsList = [];

/**
 * Set (or replace) the platform library used by contentRecipe(). Each entry
 * must be `{ id, label, defaultHref, ariaLabel, svgMarkup }`. svgMarkup is
 * parsed and sanitized in place. Entries that fail parsing are dropped with
 * a console warning.
 */
export function setSocialPlatforms(platforms) {
  _platformsById.clear();
  _platformsList = [];
  if (!Array.isArray(platforms)) return;
  for (const entry of platforms) {
    if (!entry || !entry.id) continue;
    const svg = parseSvg(entry.svgMarkup);
    if (!svg) {
      console.warn(`[social-link-adapter] skipping platform "${entry.id}" — SVG failed to parse`);
      continue;
    }
    sanitizeSvg(svg);
    // Preserve explicit dimensions from the library. The section CSS still
    // controls rendered size, while width/height keep the canonical markup
    // aligned with CodeStitch/PageSpeed expectations.
    // Always apply CodeStitch wiring at insertion time, not in the JSON.
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'cs-social-img');
    const cleaned = {
      id: String(entry.id),
      label: String(entry.label || entry.id),
      defaultHref: String(entry.defaultHref || ''),
      ariaLabel: String(entry.ariaLabel || `visit ${entry.id} profile`),
      svgMarkup: serializeSvg(svg)
    };
    _platformsById.set(cleaned.id, cleaned);
    _platformsList.push(cleaned);
  }
}

/** Return the registered platforms in registration order. */
export function getSocialPlatforms() {
  return _platformsList.slice();
}

/** Return a single platform by id, or null. */
export function getSocialPlatform(id) {
  return _platformsById.get(String(id || '')) || null;
}

function _buildAnchor(platform, opts = {}) {
  const doc = new DOMParser().parseFromString('<div></div>', 'text/html');
  const a = doc.createElement('a');
  a.setAttribute('class', 'cs-social-link');
  const href = sanitizeHref(opts.href != null ? opts.href : platform.defaultHref);
  a.setAttribute('href', href);
  a.setAttribute(
    'aria-label',
    String(opts.ariaLabel != null ? opts.ariaLabel : platform.ariaLabel)
  );

  const svgWrap = new DOMParser().parseFromString(`<div>${platform.svgMarkup}</div>`, 'text/html');
  const svgEl = svgWrap.body.firstElementChild?.firstElementChild;
  if (svgEl) {
    a.appendChild(doc.importNode(svgEl, true));
  }
  return a.outerHTML;
}

/**
 * The adapter object. Registered with pattern-adapters.register().
 */
export const socialLinkAdapter = {
  kind: 'social-link',
  label: 'Social icon',
  itemSelector: '.cs-social-link',
  parentSelector: '.cs-social',
  menuActions: ['add-after', 'duplicate', 'delete'],
  postInsertFlow: 'open-drawer',
  cssPatch: null,
  // Reorder roots differ by markup variant: direct anchors move themselves;
  // wrapped social lists move the li that owns the anchor. itemSelector stays
  // link-scoped for the editor/recipe; reorderItemSelector is mutation-only.
  reorderItemSelector: ':is(.cs-social > a.cs-social-link, ul.cs-social > li)',
  reorderSelector: ':is(.cs-social > a.cs-social-link, ul.cs-social > li)',
  reorderSiblingSelector: ':is(.cs-social > a.cs-social-link, ul.cs-social > li)',
  reorderAxis: 'flow',
  historyLabel: 'Reorder social link',
  toastText: 'Social link reordered',
  vtPrefix: 'pb-soc-',
  vtSelector: ':is(.cs-social > a.cs-social-link, ul.cs-social > li)',
  moveFn: moveSiblingPreservingEids,
  reorderable: true,

  /**
   * Match by climbing the probe to the nearest .cs-social-link ancestor.
   * Returns the matched probe entry (truthy) or null. Works against both a
   * live DOM Element and a chainProbe from the iframe payload.
   */
  match(probe) {
    if (!probe || typeof probe.closest !== 'function') return null;
    return probe.closest('.cs-social-link');
  },

  /**
   * Build a sibling <a class="cs-social-link"> markup for the given platform.
   * @param {object} opts
   * @param {string} opts.platform — id from the social-icon library
   * @param {string} [opts.href] — override; falls back to platform.defaultHref. Sanitized.
   * @param {string} [opts.ariaLabel] — override; falls back to platform.ariaLabel.
   * @returns {string|null} HTML string (single root element) or null if unknown platform.
   */
  contentRecipe(opts) {
    const platform = getSocialPlatform(opts?.platform);
    if (!platform) return null;
    return _buildAnchor(platform, opts || {});
  }
};
